/**
 * LeftShield Portal - Framework Detail Page
 * Route: /frameworks/:referenceId | Category: Compliance
 *
 * §12746 Wave 2 — full GUID-routed detail page for `ComplianceFramework`, replacing
 * `FrameworkDetailDrawer` for direct-link / deep-link access. Ports the drawer's entire
 * feature set (source-link, compliance score, stat tiles, last-assessed metadata, and the
 * expandable controls list with its own sub-fetch) into a `PageHeader` + `usePageStyles` Card
 * shell, per `.claude/rules/feature-porting.md`.
 *
 * PARITY NOTES — every field/section the drawer rendered is ported. Nothing is intentionally
 * omitted; see inline comments below for the one-to-one mapping from
 * `FrameworkDetailDrawer.tsx`.
 */
import React, { useState } from 'react';
import {
  Title2,
  Body1,
  Body2,
  Caption1,
  Card,
  Badge,
  Button,
  Spinner,
  ProgressBar,
  Link,
  tokens,
  makeStyles,
} from '@fluentui/react-components';
import {
  ShieldCheckmarkRegular,
  ErrorCircleRegular,
  ChevronDownRegular,
  ChevronRightRegular,
  OpenRegular,
} from '@fluentui/react-icons';
import { useParams, useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { usePageStyles } from '../../styles/page-styles';
import { PageHeader } from '../../components/common/PageHeader';
import { complianceApi, frameworksApi } from '../../services/api';
import { DetailSection } from '../../components/common/DetailDrawer';
import type { ControlDetail } from '../../types';

/** Compliance Cyan — `brand/brand-config.json` → `colors.categoryThemes.compliance.primary`. */
const FRAMEWORK_COLOR = '#13B5B1';

const getScoreColor = (score: number): string => {
  if (score >= 90) return tokens.colorPaletteGreenForeground1;
  if (score >= 70) return tokens.colorPaletteYellowForeground1;
  if (score >= 50) return tokens.colorPaletteRedForeground2;
  return tokens.colorPaletteRedForeground1;
};

// ---------------------------------------------------------------------------
// Ported from FrameworkDetailDrawer.tsx — severity + control-row helpers
// ---------------------------------------------------------------------------

const severityLabel = (severity: string | number): string => {
  if (typeof severity === 'number') {
    return ['Info', 'Low', 'Medium', 'High', 'Critical'][severity] ?? `${severity}`;
  }
  return severity;
};

const severityBadgeColor = (
  severity: string | number,
): 'danger' | 'warning' | 'important' | 'informative' => {
  const label = severityLabel(severity);
  switch (label) {
    case 'Critical':
    case 'High':
      return 'danger';
    case 'Medium':
      return 'warning';
    case 'Low':
      return 'important';
    default:
      return 'informative';
  }
};

const useLocalStyles = makeStyles({
  controlList: {
    display: 'flex',
    flexDirection: 'column',
    gap: tokens.spacingVerticalXS,
  },
  controlHeader: {
    display: 'flex',
    alignItems: 'center',
    gap: tokens.spacingHorizontalS,
    padding: tokens.spacingVerticalS,
    borderRadius: tokens.borderRadiusSmall,
    backgroundColor: tokens.colorNeutralBackground2,
    cursor: 'pointer',
    ':hover': {
      backgroundColor: tokens.colorNeutralBackground2Hover,
    },
  },
  controlHeaderText: {
    flex: 1,
    minWidth: 0,
  },
  controlBadges: {
    display: 'flex',
    gap: tokens.spacingHorizontalXS,
    flexShrink: 0,
    alignItems: 'center',
  },
  controlExpanded: {
    padding: `${tokens.spacingVerticalS} ${tokens.spacingVerticalM}`,
    paddingLeft: tokens.spacingHorizontalXXL,
    display: 'flex',
    flexDirection: 'column',
    gap: tokens.spacingVerticalS,
    borderLeft: `2px solid ${tokens.colorNeutralStroke1}`,
    marginLeft: tokens.spacingHorizontalM,
    marginBottom: tokens.spacingVerticalXS,
  },
  detailBlock: {
    display: 'flex',
    flexDirection: 'column',
    gap: tokens.spacingVerticalXXS,
  },
  meta: {
    display: 'flex',
    gap: tokens.spacingHorizontalM,
    color: tokens.colorNeutralForeground3,
    flexWrap: 'wrap',
  },
  scoreRow: {
    display: 'flex',
    alignItems: 'center',
    gap: tokens.spacingHorizontalM,
  },
  centeredState: {
    display: 'flex',
    justifyContent: 'center',
    padding: tokens.spacingVerticalL,
  },
});

/** Ported verbatim from `FrameworkDetailDrawer.tsx`'s `ControlRow` — expandable control row
 *  with description / implementation guidance / testing procedures. */
function ControlRow({ control }: { control: ControlDetail }) {
  const styles = useLocalStyles();
  const [expanded, setExpanded] = useState(false);

  const hasDetails =
    control.description ||
    control.implementationGuidance ||
    control.testingProcedures ||
    control.section ||
    control.category;

  return (
    <div>
      <div
        className={styles.controlHeader}
        onClick={() => hasDetails && setExpanded(!expanded)}
        role={hasDetails ? 'button' : undefined}
        tabIndex={hasDetails ? 0 : undefined}
        onKeyDown={(e) => {
          if (hasDetails && (e.key === 'Enter' || e.key === ' ')) {
            e.preventDefault();
            setExpanded(!expanded);
          }
        }}
      >
        {hasDetails ? (
          expanded ? (
            <ChevronDownRegular style={{ fontSize: '12px', flexShrink: 0 }} />
          ) : (
            <ChevronRightRegular style={{ fontSize: '12px', flexShrink: 0 }} />
          )
        ) : (
          <span style={{ width: '12px', flexShrink: 0 }} />
        )}
        <div className={styles.controlHeaderText}>
          <Body2 style={{ fontWeight: 600 }}>
            {control.controlId} — {control.name}
          </Body2>
        </div>
        <div className={styles.controlBadges}>
          <Badge appearance="tint" color={severityBadgeColor(control.severity)} size="small">
            {severityLabel(control.severity)}
          </Badge>
        </div>
      </div>

      {expanded && (
        <div className={styles.controlExpanded}>
          {(control.section || control.category) && (
            <div className={styles.meta}>
              {control.section && <Caption1>{control.section}</Caption1>}
              {control.category && (
                <Badge appearance="outline" size="small">
                  {control.category}
                </Badge>
              )}
            </div>
          )}

          {control.description && (
            <div className={styles.detailBlock}>
              <Caption1 style={{ color: tokens.colorNeutralForeground3 }}>Description</Caption1>
              <Body2>{control.description}</Body2>
            </div>
          )}

          {control.implementationGuidance && (
            <div className={styles.detailBlock}>
              <Caption1 style={{ color: tokens.colorNeutralForeground3 }}>
                Implementation Guidance
              </Caption1>
              <Body2>{control.implementationGuidance}</Body2>
            </div>
          )}

          {control.testingProcedures && (
            <div className={styles.detailBlock}>
              <Caption1 style={{ color: tokens.colorNeutralForeground3 }}>
                Testing Procedures
              </Caption1>
              <Body2>{control.testingProcedures}</Body2>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Page
// ---------------------------------------------------------------------------

export const FrameworkDetailPage: React.FC = () => {
  const ps = usePageStyles();
  const ls = useLocalStyles();
  const { referenceId } = useParams<{ referenceId: string }>();
  const navigate = useNavigate();

  const {
    data: framework,
    isLoading,
    isError,
    refetch,
  } = useQuery({
    queryKey: ['framework-by-reference-id', referenceId],
    queryFn: () => complianceApi.getFrameworkByReferenceId(referenceId!),
    enabled: !!referenceId,
  });

  // Ported from `FrameworkDetailDrawer.tsx` — controls sub-fetch, keyed by framework code once
  // the framework itself has loaded (drawer used `open && !!framework?.code`; here `!!framework?.code`
  // alone gates it since the page has no separate open/closed state).
  const { data: controlResult, isLoading: controlsLoading } = useQuery({
    queryKey: ['frameworkControls', framework?.code],
    queryFn: () => frameworksApi.getControls(framework!.code),
    enabled: !!framework?.code,
  });

  const controls = controlResult?.controls;
  const sourceUrl = controlResult?.sourceUrl;

  const backAction = (
    <Button appearance="subtle" onClick={() => navigate('/frameworks')}>
      Back to Frameworks
    </Button>
  );

  if (!referenceId) {
    return (
      <div className={ps.page}>
        <PageHeader
          title="Framework Detail"
          subtitle="No framework reference ID was provided."
          icon={<ShieldCheckmarkRegular />}
          iconColor={FRAMEWORK_COLOR}
          actions={backAction}
        />
      </div>
    );
  }

  if (isLoading) {
    return (
      <div className={ps.page}>
        <PageHeader
          title="Framework Detail"
          subtitle="Loading enforcement framework…"
          icon={<ShieldCheckmarkRegular />}
          iconColor={FRAMEWORK_COLOR}
        />
        <Card>
          <div className={ps.cardBody}>
            <Spinner label="Loading framework…" />
          </div>
        </Card>
      </div>
    );
  }

  if (isError || !framework) {
    return (
      <div className={ps.page}>
        <PageHeader
          title="Framework Detail"
          subtitle={`Framework ${referenceId}`}
          icon={<ShieldCheckmarkRegular />}
          iconColor={FRAMEWORK_COLOR}
          actions={backAction}
        />
        <Card>
          <div className={ps.cardBody}>
            <ErrorCircleRegular style={{ fontSize: '32px', color: tokens.colorPaletteRedForeground1 }} />
            <Body1>Unable to load this framework. It may not exist, or you may not have access to it.</Body1>
            <Button appearance="secondary" onClick={() => refetch()}>
              Retry
            </Button>
          </div>
        </Card>
      </div>
    );
  }

  const f = framework;

  return (
    <div className={ps.page}>
      <PageHeader
        title="Framework Detail"
        subtitle={`Enforcement framework — ${f.name}`}
        icon={<ShieldCheckmarkRegular />}
        iconColor={FRAMEWORK_COLOR}
        actions={backAction}
      />

      <Card>
        <div className={ps.cardBody}>
          <div className={ps.sectionHeader}>
            <Title2>{f.name}</Title2>
            <Badge appearance="outline">{f.code}</Badge>
            <Badge appearance="tint" color="informative">
              v{f.version}
            </Badge>
            {f.isEnabled ? (
              <Badge appearance="filled" color="success">
                Enabled
              </Badge>
            ) : (
              <Badge appearance="tint" color="warning">
                Disabled
              </Badge>
            )}
          </div>

          {f.description && <Body1>{f.description}</Body1>}

          {/* Ported from drawer — official source document link, driven by the controls
              sub-fetch's `sourceUrl`. */}
          {sourceUrl && (
            <Link href={sourceUrl} target="_blank" rel="noopener noreferrer">
              <OpenRegular style={{ marginRight: tokens.spacingHorizontalXS }} />
              View official source document
            </Link>
          )}

          {/* Ported from drawer — Compliance Score section (DetailSection + ProgressBar) */}
          <DetailSection title="Compliance Score">
            <div className={ls.scoreRow}>
              <ProgressBar value={(f.complianceScore ?? 0) / 100} thickness="large" style={{ flex: 1 }} />
              <Title2 style={{ color: getScoreColor(f.complianceScore ?? 0) }}>
                {f.complianceScore ?? 0}%
              </Title2>
            </div>
          </DetailSection>

          <div className={ps.statGrid}>
            <div className={ps.statTile}>
              <Caption1 style={{ color: tokens.colorNeutralForeground3 }}>Controls</Caption1>
              <Body1 style={{ fontWeight: 600 }}>{f.controlCount}</Body1>
            </div>
            <div className={ps.statTile}>
              <Caption1 style={{ color: tokens.colorPaletteGreenForeground1 }}>Passed</Caption1>
              <Body1 style={{ fontWeight: 600, color: tokens.colorPaletteGreenForeground1 }}>
                {f.passedCount ?? 0}
              </Body1>
            </div>
            <div className={ps.statTile}>
              <Caption1 style={{ color: tokens.colorPaletteRedForeground1 }}>Failed</Caption1>
              <Body1 style={{ fontWeight: 600, color: tokens.colorPaletteRedForeground1 }}>
                {f.failedCount ?? 0}
              </Body1>
            </div>
          </div>

          {f.lastAssessedAt && (
            <Caption1 style={{ color: tokens.colorNeutralForeground3 }}>
              Last assessed: {new Date(f.lastAssessedAt).toLocaleDateString()}
            </Caption1>
          )}

          <Caption1 style={{ color: tokens.colorNeutralForeground3 }}>
            Reference ID: {f.referenceId}
          </Caption1>
        </div>
      </Card>

      {/* Ported from drawer — expandable Controls List section, with its own loading/empty
          states driven by the `frameworksApi.getControls` sub-fetch. */}
      <Card>
        <div className={ps.cardBody}>
          <DetailSection title={`Controls (${controls?.length ?? f.controlCount})`}>
            {controlsLoading ? (
              <div className={ls.centeredState}>
                <Spinner size="small" label="Loading controls..." />
              </div>
            ) : controls && controls.length > 0 ? (
              <div className={ls.controlList}>
                {controls.map((control: ControlDetail) => (
                  <ControlRow key={control.id} control={control} />
                ))}
              </div>
            ) : (
              <Body2
                style={{
                  color: tokens.colorNeutralForeground3,
                  textAlign: 'center',
                  padding: tokens.spacingVerticalL,
                }}
              >
                No controls found for this framework.
              </Body2>
            )}
          </DetailSection>
        </div>
      </Card>
    </div>
  );
};
